import {DEFAULT_LOCALE, LOCALE_CODES, type Locale} from './locales';

/** Public pages that have their own address per language: `/price`, `/es/price`, `/de/price`. */
export const LOCALIZED_PATHS = ['/', '/price', '/gallery'] as const;

export type LocalizedPath = (typeof LOCALIZED_PATHS)[number];

/** Locales that appear as a URL prefix, i.e. every locale except the default. */
export const PREFIXED_LOCALES = LOCALE_CODES.filter((locale) => locale !== DEFAULT_LOCALE);

export interface LocalePath {
    locale: Locale;
    /** The English (unprefixed) address of the same page. */
    basePath: LocalizedPath;
}

function trimTrailingSlash(path: string): string {
    return path.length > 1 ? path.replace(/\/+$/, '') || '/' : path;
}

function asLocalizedPath(path: string): LocalizedPath | null {
    return (LOCALIZED_PATHS as readonly string[]).includes(path) ? (path as LocalizedPath) : null;
}

/**
 * Reads the language of a localized public page from its pathname. Returns null
 * for every page that has no language versions (the app, gallery projects, docs).
 */
export function parseLocalePath(pathname: string): LocalePath | null {
    const path = trimTrailingSlash(pathname);
    const [, first = '', ...rest] = path.split('/');
    const prefixed = PREFIXED_LOCALES.find((locale) => locale === first);
    if (prefixed) {
        const basePath = asLocalizedPath(`/${rest.join('/')}`);
        return basePath ? {locale: prefixed, basePath} : null;
    }
    const basePath = asLocalizedPath(path);
    return basePath ? {locale: DEFAULT_LOCALE, basePath} : null;
}

/** Builds the address of a localized public page in a language: `/price` + `es` → `/es/price`, `/` + `es` → `/es/`. */
export function localizePath(basePath: LocalizedPath, locale: Locale): string {
    if (locale === DEFAULT_LOCALE) return basePath;
    return basePath === '/' ? `/${locale}/` : `/${locale}${basePath}`;
}

/**
 * Points an internal link at the given language: `/gallery#trending` + `de` →
 * `/de/gallery#trending`. Links to pages without language versions (the app,
 * gallery projects, docs, other sites) come back unchanged.
 */
export function localizeHref(href: string, locale: Locale): string {
    const suffixStart = href.search(/[?#]/);
    const path = suffixStart === -1 ? href : href.slice(0, suffixStart);
    const suffix = suffixStart === -1 ? '' : href.slice(suffixStart);
    const page = path.startsWith('/') ? parseLocalePath(path) : null;
    return page ? localizePath(page.basePath, locale) + suffix : href;
}
